import React, { useEffect, useState, useContext } from "react";
import "./VoyahFreeHero.css";

import { LanguageContext } from "../../../../context/LanguageContext";
import { getPageContent } from "../../../../api/publicContent.api";
import LanguageSelector from "../../../LanguageSelector";

import back from "../../../../assets/images/voyahfree/hero/background.png";

const parseStatLines = (text) =>
  String(text || "")
    .split(/\r?\n|\\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const VoyahFreeHero = () => {
  const { currentLang } = useContext(LanguageContext);

  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await getPageContent("voyahfree", currentLang);

        const heroSection = response?.sections?.hero;

        if (!heroSection) {
          console.error("Hero section missing in CMS response");
          setError(true);
          return;
        }

        setHero(heroSection);
      } catch (err) {
        console.error("CMS load error:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [currentLang]);

  if (loading || error || !hero) return null;
  const preferredOrder = ["range", "acceleration", "power"];
  const statPairsFromLabelValue = Object.keys(hero)
    .filter((k) => k.endsWith("_label"))
    .map((labelKey) => {
      const baseKey = labelKey.replace(/_label$/, "");
      const valueKey = `${baseKey}_value`;
      return {
        key: baseKey,
        label: hero[labelKey],
        value: hero[valueKey],
      };
    })
    .filter((item) => item.label || item.value)
    .sort((a, b) => {
      const ia = preferredOrder.indexOf(a.key);
      const ib = preferredOrder.indexOf(b.key);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  const statPairsFromStatKeys = Object.keys(hero)
    .filter((k) => /^stat_\d+$/.test(k))
    .sort((a, b) => Number(a.split("_")[1]) - Number(b.split("_")[1]))
    .map((k) => String(hero[k] || "").trim())
    .filter(Boolean)
    .map((text, idx) => {
      const sepIndex = text.indexOf(":");
      if (sepIndex !== -1) {
        return {
          key: `stat_${idx + 1}`,
          label: `${text.slice(0, sepIndex).trim()}:`,
          value: text.slice(sepIndex + 1).trim(),
        };
      }
      return {
        key: `stat_${idx + 1}`,
        label: text,
        value: "",
      };
    });
  const statPairs = statPairsFromLabelValue.length
    ? statPairsFromLabelValue
    : statPairsFromStatKeys;

  const displayStats = [...statPairs];
  const trailingSingles = [];

  while (displayStats.length > 0) {
    const last = displayStats[displayStats.length - 1];
    const lastLines = parseStatLines(last.value || last.label);

    if (!last.value && last.label && lastLines.length === 1) {
      trailingSingles.unshift(last);
      displayStats.pop();
    } else {
      break;
    }
  }

  if (trailingSingles.length > 1) {
    displayStats.push({
      key: trailingSingles.map((item) => item.key).join("-"),
      label: "",
      value: trailingSingles.map((item) => item.label).join("\n"),
    });
  } else if (trailingSingles.length === 1) {
    displayStats.push(trailingSingles[0]);
  }

  return (
    <section className="voyahfreehero">
      {/* FULLSCREEN IMAGE */}
      <img
        src={back}
        alt="Voyah Free Hero"
        className="voyahfreehero-bg"
      />

      <div className="absolute top-6 right-6 z-50">
        <LanguageSelector variant="dark" />
      </div>

      {/* STATS */}
      <div className="voyahfreehero-stats">
        {displayStats.map((item, index) => {
          const isLast = index === displayStats.length - 1;
          const multilineText = [item.value, item.label].filter(Boolean).join("\n");
          const lines = parseStatLines(multilineText);

          if (isLast && lines.length > 1) {
            return (
              <div key={item.key} className="voyah-stat voyah-stat-multiline">
                {lines.map((line, lineIndex) => (
                  <span key={lineIndex} className="voyah-stat-line">
                    {line}
                  </span>
                ))}
              </div>
            );
          }

          return (
            <div key={item.key} className="voyah-stat">
              {item.value ? (
                <>
                  {item.label ? (
                    <span className="voyah-stat-label">{item.label}</span>
                  ) : null}
                  <span className="voyah-stat-value">{item.value}</span>
                </>
              ) : (
                <span className="voyah-stat-single">{item.label}</span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default VoyahFreeHero;
